export type TFieldErrors = Record<string, string[]>;

export const CARD_HOLDER_PHONE_TAKEN_ERROR_CODE = 2202;
export const CARD_HOLDER_REJECTED_ERROR_CODE = 2203;

type TMaybeError = {
	code?: number;
	message?: unknown;
	details?: unknown;
	stack?: unknown;
	error?: unknown;
	missing_fields?: unknown;
	card_holder_uid?: unknown;
};

const TECHNICAL_MESSAGE_PATTERNS = [
	/provider\s+error/i,
	/issuer\s*\d+\s*response\s*error/i,
	/not supported/i,
	/wrong params/i,
	/validation error/i,
	/card holder information incomplete/i,
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

const getString = (value: unknown): string | undefined => {
	if (typeof value === "string") return value;
	if (typeof value === "number" || typeof value === "boolean") return String(value);
	return undefined;
};

/** Adds a quoted field path so FormParser highlights the editable phone input. */
export const attachPhoneTakenField = (error: IError, field: "phone" | "mobile"): IError =>
	error.code === CARD_HOLDER_PHONE_TAKEN_ERROR_CODE
		? { ...error, details: `"${field}" ${error.message}` }
		: error;

export const isKycPhoneCorrectionError = (error: unknown): boolean => {
	const normalized = unwrapError(error);
	return normalized?.code === CARD_HOLDER_PHONE_TAKEN_ERROR_CODE || normalized?.code === 2614;
};

/** kyc/start 409: our KYC is already approved, so the step is done — re-read status instead of toasting. */
export const isKycAlreadyApproved = (error: unknown): boolean => unwrapError(error)?.code === 2615;

export const unwrapError = (error: unknown): TMaybeError | null => {
	if (error === null || error === undefined) return null;

	if (typeof error === "string" || typeof error === "number" || typeof error === "boolean") {
		return { code: 0, message: String(error) };
	}

	if (!isRecord(error)) {
		try {
			return { code: 1, message: JSON.stringify(error) };
		} catch {
			return { code: 2, message: "unknown error" };
		}
	}

	const record = error as TMaybeError & Record<string, unknown>;

	if (record.error) {
		const nested = unwrapError(record.error);
		if (nested) return nested;
	}

	if (typeof record.message === "string" || typeof record.message === "number" || typeof record.message === "boolean") {
		return {
			code: typeof record.code === "number" ? record.code : 0,
			message: String(record.message),
			details: record.details,
			stack: record.stack,
			missing_fields: record.missing_fields,
			card_holder_uid: record.card_holder_uid,
		};
	}

	if (record.details !== undefined) {
		const details = getString(record.details);
		if (details) {
			return {
				code: typeof record.code === "number" ? record.code : 0,
				message: details,
				details: record.details,
				stack: record.stack,
				missing_fields: record.missing_fields,
				card_holder_uid: record.card_holder_uid,
			};
		}
	}

	try {
		return { code: 1, message: JSON.stringify(record) };
	} catch {
		return { code: 2, message: "unknown error" };
	}
};

const extractPathKey = (detail: unknown): string | null => {
	const text = String(detail);
	const match = text.match(/["']([^"']+)["']/);
	if (!match) return null;

	const path = match[1]
		.replace(/\[(\d+)\]/g, ".$1")
		.split(".")
		.filter(Boolean)
		.filter(part => part !== "data");

	if (path.length === 0) return null;
	return path[path.length - 1];
};

const hasFieldKey = (value: unknown, fieldKey: string): boolean => {
	if (!isRecord(value)) return false;

	if (Object.prototype.hasOwnProperty.call(value, fieldKey)) return true;

	for (const key of Object.keys(value)) {
		const current = value[key];
		if (Array.isArray(current)) {
			if (current.some(item => hasFieldKey(item, fieldKey))) return true;
			continue;
		}
		if (isRecord(current) && hasFieldKey(current, fieldKey)) return true;
	}

	return false;
};

export const parseSubmitError = (
	submitError: unknown,
	formData: unknown
): { fieldErrors: TFieldErrors; topError: TMaybeError | null } => {
	const normalized = unwrapError(submitError);
	if (!normalized) return { fieldErrors: {}, topError: null };

	const details = Array.isArray(normalized.details) ? normalized.details : [];
	if (details.length === 0) {
		return { fieldErrors: {}, topError: normalized };
	}

	const fieldErrors: TFieldErrors = {};
	const remainingDetails: unknown[] = [];

	for (const detail of details) {
		const fieldKey = extractPathKey(detail);
		if (!fieldKey || !hasFieldKey(formData, fieldKey)) {
			remainingDetails.push(detail);
			continue;
		}

		if (!fieldErrors[fieldKey]) fieldErrors[fieldKey] = [];
		fieldErrors[fieldKey].push(String(detail));
	}

	const topError = remainingDetails.length > 0 ? { ...normalized, details: remainingDetails } : { ...normalized, details: undefined };
	return { fieldErrors, topError };
};

export const getErrorDetailsText = (details: unknown): string => {
	if (typeof details === "string" || typeof details === "number" || typeof details === "boolean") return String(details);

	if (Array.isArray(details)) {
		const allPrimitive = details.every(val => ["string", "number", "boolean"].includes(typeof val));
		if (allPrimitive) return details.map(String).join(", ");
		try {
			return JSON.stringify(details);
		} catch {
			return "unknown error";
		}
	}

	if (isRecord(details)) {
		try {
			const seen = new WeakSet<object>();
			return JSON.stringify(details, (_key, value) => {
				if (typeof value === "object" && value !== null) {
					if (seen.has(value)) return "[Circular]";
					seen.add(value);
				}
				return value;
			});
		} catch {
			return "unknown error";
		}
	}

	return String(details);
};

export const isUnsupportedCardHolderRegionError = (error: unknown): boolean => {
	const normalized = unwrapError(error);
	if (!normalized || normalized.code !== 2201) return false;

	const details = getErrorDetailsText(normalized.details).toLowerCase();
	return details.includes("does not support the selected country/region") ||
		details.includes("unsupported country/region");
};

export const getFriendlyErrorMessage = (error: unknown, fallback = "Something went wrong"): string => {
	const normalized = unwrapError(error);
	if (!normalized) return fallback;

	const message = getString(normalized.message) ?? getString(normalized.details) ?? fallback;
	const lower = message.toLowerCase();
	if (normalized.code === CARD_HOLDER_REJECTED_ERROR_CODE) return message || fallback;
	if (normalized.code === 2613 && !lower.includes("kyc verification required")) return message || fallback;
	const searchableError = `${message} ${getErrorDetailsText(normalized.details ?? "")}`.toLowerCase();
	if (searchableError.includes("kyc verification required") || searchableError.includes("complete kyc via kyc/start")) {
		return "Complete identity verification to unlock account activity and card features.";
	}
	if (isUnsupportedCardHolderRegionError(normalized)) {
		return "This card provider cannot issue cards for your country or region. Choose another card type or contact support.";
	}
	if (normalized.code === 2201 && getErrorDetailsText(normalized.details).toLowerCase().includes("rejected")) {
		const genericMessages = ["card error", "provider error", "card holder information incomplete"];
		if (!genericMessages.includes(lower) && message.trim()) return message;
		return "The card provider rejected the card holder details. Review the entered information or contact support.";
	}

	if (normalized.code === 2201 || Array.isArray(normalized.missing_fields) || lower.includes("card holder information incomplete")) {
		const details = getErrorDetailsText(normalized.details).toLowerCase();
		if (details.includes("kyc/phone")) {
			return "Add your phone number before continuing identity verification.";
		}
		if (details.includes("kyc/start")) {
			return "Identity verification must be completed again before issuing the card.";
		}
		if (normalized.code === 2201 && (!Array.isArray(normalized.missing_fields) || normalized.missing_fields.length === 0)) {
			return message || fallback;
		}
		return "Complete card holder details before issuing the card.";
	}

	if (normalized.code === 1051) {
		return "No issuer account is enabled. Contact support to continue.";
	}

	if (/issuer\s*\d+\s*response\s*error/i.test(message)) {
		return "The card issuer returned an error. Please try again.";
	}

	if (/provider\s+error/i.test(message)) {
		return "The provider returned an error. Please try again.";
	}

	if (/not supported/i.test(message)) {
		return "This action is not supported for this card provider.";
	}

	if (/wrong params/i.test(message) || /validation error/i.test(message)) {
		return "Please review the highlighted fields and try again.";
	}

	if (normalized.details && typeof normalized.details === "string") {
		const details = normalized.details.trim();
		const detailsLower = details.toLowerCase();
		if (details.length <= 160 && !TECHNICAL_MESSAGE_PATTERNS.some(pattern => pattern.test(detailsLower))) {
			return details;
		}
	}

	if (lower === "unknown error" || lower === "error") return fallback;

	return message || fallback;
};

export const formatInlineFieldErrors = (messages?: string[]): string => {
	if (!messages || messages.length === 0) return "";
	return messages.map(message => message.trim()).filter(Boolean).join(", ");
};
